/* ============================================================
   AuthContext — two ways into the same Cognito user pool.

   1. Email + password (the default) runs natively against the cognito-idp API
      (see cognitoApi.ts). No browser, no hosted page: sign-up, verification,
      sign-in and password reset are all screens in the front end.

   2. Social (Apple / Google / Facebook) goes through OAuth2 authorization-code
      + PKCE, because federating an IdP through a user pool is only possible via
      the hosted /oauth2/authorize endpoint. We pass `identity_provider`, which
      makes Cognito redirect straight to the provider instead of rendering its
      own sign-in page — so the user taps our button and lands on Apple's or
      Google's sheet, never on an AWS-branded page. Opening that browser and
      catching the redirect is the ONE platform-specific step, and it is the
      OAuthAdapter's (adapters.ts); the code exchange, refresh and revoke are
      plain form POSTs the core makes itself (oauthApi.ts).

   Both paths end in the same place: tokens in the platform's secure store
   (tokens.ts), silently refreshed. Each session records how it was created
   (`origin`) so it refreshes through the matching front door.
   ============================================================ */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useClientCore } from '../provider';
import { discoveryFor, IDP, type OAuthDiscovery, type SocialProvider } from './config';
import { CognitoError, type CognitoApi, type CognitoTokens, type SignUpResult } from './cognitoApi';
import { exchangeCode, refreshOAuthTokens, revokeToken } from './oauthApi';
import type { StoredTokens } from './tokens';

// Refresh this far before the token actually expires, so an in-flight request
// never races the expiry boundary.
const REFRESH_SKEW_MS = 60_000;

/** An ID token for `t` that stores nothing. Unlike `refreshSession`, it never
    writes the refreshed tokens back. A refresh that lands after sign-out has
    moved on would otherwise re-save the session sign-out just cleared. */
async function idTokenWithoutStoring(
  t: StoredTokens,
  deps: { cognito: CognitoApi; clientId: string; discovery: OAuthDiscovery },
): Promise<string | null> {
  if (t.expiresAt - Date.now() > REFRESH_SKEW_MS) return t.idToken;
  if (!t.refreshToken) return null;
  if (t.origin === 'password') return (await deps.cognito.refreshTokens(t.refreshToken)).idToken;
  const fresh = await refreshOAuthTokens(deps.discovery.tokenEndpoint, {
    clientId: deps.clientId,
    refreshToken: t.refreshToken,
  });
  return fresh.idToken ?? null;
}

export interface AuthUser {
  sub?: string;
  email?: string;
}

export type AuthStatus = 'loading' | 'signedOut' | 'signedIn';

export interface AuthValue {
  status: AuthStatus;
  user: AuthUser | null;
  error: string | null;
  /** clear the banner — screens call this when the user edits a field */
  clearError: () => void;
  /** open the browser at the given provider (skips Cognito's own page) */
  signInWithProvider: (provider: SocialProvider) => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<SignUpResult>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendConfirmationCode: (email: string) => Promise<string | null>;
  forgotPassword: (email: string) => Promise<string | null>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Permanently delete the Cognito account, then sign out locally. Only the
     IDENTITY — the caller must already have erased the server's rows through
     `DELETE /v1/me`; see docs/account-deletion.md for why in that order. */
  deleteAccount: () => Promise<void>;
  /** a fresh ID token for the API Authorization header (refreshes if stale) */
  getIdToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthValue | null>(null);

/** Decode a JWT payload without a crypto dep (we don't verify — the API does). */
function decodeJwt(token: string): Record<string, unknown> {
  try {
    const part = token.split('.')[1];
    const atobFn = (globalThis as { atob?: (s: string) => string }).atob;
    if (!part || typeof atobFn !== 'function') return {};
    const bin = atobFn(part.replace(/-/g, '+').replace(/_/g, '/'));
    let pct = '';
    for (let i = 0; i < bin.length; i++) {
      pct += '%' + ('00' + bin.charCodeAt(i).toString(16)).slice(-2);
    }
    return JSON.parse(decodeURIComponent(pct)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function userFromIdToken(idToken: string): AuthUser {
  const claims = decodeJwt(idToken);
  return {
    sub: typeof claims.sub === 'string' ? claims.sub : undefined,
    email: typeof claims.email === 'string' ? claims.email : undefined,
  };
}

/** Did this session come through the hosted UI (Google/Apple) rather than the
   native password screens?

   It decides whether signOut has to take the browser detour below. Read off the
   token rather than tracked as state: a federated id token carries an
   `identities` array, and `cognito:username` is provider-prefixed
   (`Google_1166…`) — so the answer survives app restarts for free, because the
   token is already persisted. The username check is a belt-and-braces fallback
   in case a provider's mapping ever leaves `identities` off. */
function isFederatedSession(idToken: string): boolean {
  const claims = decodeJwt(idToken);
  if (Array.isArray(claims.identities) && claims.identities.length > 0) return true;
  const username = claims['cognito:username'];
  return (
    typeof username === 'string' &&
    Object.values(IDP).some((p) => username.toLowerCase().startsWith(`${p.toLowerCase()}_`))
  );
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { config, adapters, tokens: tokenStore, cognito: cognitoApi } = useClientCore();
  const { clientId, scopes } = config.cognito;
  const discovery = useMemo(() => discoveryFor(config.cognito.domain), [config.cognito.domain]);
  const { analytics, billing, oauth } = adapters;

  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Source of truth for the current tokens, read by getIdToken without stale
  // closures. State above is only for rendering.
  const tokensRef = useRef<StoredTokens | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const applyTokens = useCallback(
    async (t: StoredTokens) => {
      tokensRef.current = t;
      await tokenStore.save(t);
      const u = userFromIdToken(t.idToken);
      setUser(u);
      setError(null);
      setStatus('signedIn');
    },
    [tokenStore],
  );

  // Tell the billing SDK WHO this is, keyed by Cognito `sub` rather than the
  // anonymous device id (docs/subscriptions.md §7). A device-keyed entitlement
  // breaks on reinstall, on a second device, and on every social sign-in that
  // the PreSignUp trigger merges into an existing account.
  //
  // ⚠️ Keyed on the RESOLVED USER, not placed in `applyTokens`, and that is the
  // whole point. There is more than one way to acquire a user: a fresh sign-in
  // goes through `applyTokens`, but a launch with an unexpired stored token
  // calls `setUser` directly (see the restore effect below) and never touches
  // it. An identify living in `applyTokens` was therefore skipped on the most
  // common launch of all, leaving the SDK unconfigured — a paywall with no
  // plans to sell, and a Restore Purchases button that could not work. Deriving
  // it from state makes every present and future path correct by construction.
  //
  // Deliberately NOT awaited, and the adapter swallows its own errors: the
  // billing SDK sits beside the auth path, never in front of it. Someone must
  // be able to sign in and use the app they already paid for even if the
  // billing service is unreachable — the same fail-open rule as §5.
  const billingSub = user?.sub ?? null;
  useEffect(() => {
    if (billingSub) void billing?.identify(billingSub);
  }, [billingSub, billing]);

  // Analytics keys on the same resolved `sub`, for the same reason — and,
  // unlike billing, it also follows it back to null on sign-out, so the next
  // account on this device doesn't inherit the previous one's user id.
  useEffect(() => {
    analytics?.identify(billingSub);
  }, [billingSub, analytics]);

  /** Store a hosted-UI token set. Cognito omits the refresh token on refresh —
     keep the one we have. */
  const persistOAuthTokens = useCallback(
    async (fresh: CognitoTokens) => {
      await applyTokens({
        ...fresh,
        refreshToken: fresh.refreshToken ?? tokensRef.current?.refreshToken ?? null,
        origin: 'oauth',
      });
    },
    [applyTokens],
  );

  /** Refresh through whichever front door minted the session. Returns the fresh
     id token, or null when the refresh token is gone/expired/revoked. */
  const refreshSession = useCallback(
    async (t: StoredTokens): Promise<string | null> => {
      if (!t.refreshToken) return null;
      try {
        if (t.origin === 'password') {
          const fresh = await cognitoApi.refreshTokens(t.refreshToken);
          await applyTokens({
            ...fresh,
            // InitiateAuth doesn't reissue a refresh token — keep ours
            refreshToken: fresh.refreshToken ?? t.refreshToken,
            origin: 'password',
          });
        } else {
          const fresh = await refreshOAuthTokens(discovery.tokenEndpoint, {
            clientId,
            refreshToken: t.refreshToken,
          });
          await persistOAuthTokens(fresh);
        }
        return tokensRef.current?.idToken ?? null;
      } catch {
        return null;
      }
    },
    [applyTokens, persistOAuthTokens, cognitoApi, discovery, clientId],
  );

  // Restore a session on launch, refreshing if the stored token is stale.
  useEffect(() => {
    let alive = true;
    (async () => {
      let stored: StoredTokens | null;
      try {
        stored = await tokenStore.load();
      } catch {
        // An unreadable keychain must not hold launch on `loading` — that is a
        // splash screen with nothing under it, forever. Land on sign-in and leave
        // whatever is stored alone; a fresh sign-in overwrites it.
        stored = null;
      }
      if (!alive) return;
      if (!stored) {
        setStatus('signedOut');
        return;
      }
      tokensRef.current = stored;
      if (stored.expiresAt - Date.now() > REFRESH_SKEW_MS) {
        setUser(userFromIdToken(stored.idToken));
        setStatus('signedIn');
        return;
      }
      const refreshed = await refreshSession(stored);
      if (!alive) return;
      if (refreshed) return; // applyTokens already flipped us to signedIn
      await tokenStore.clear();
      if (alive) {
        tokensRef.current = null;
        setStatus('signedOut');
      }
    })();
    return () => {
      alive = false;
    };
  }, [refreshSession, tokenStore]);

  /* ---------- social ----------
     The adapter opens the browser at Cognito's /oauth2/authorize with
     `identity_provider` set and hands back the code (plus the PKCE verifier and
     the redirect URI it used); the exchange is ours. */
  const signInWithProvider = useCallback(
    async (provider: SocialProvider) => {
      setError(null);
      try {
        const result = await oauth.authorize({
          authorizationEndpoint: discovery.authorizationEndpoint,
          clientId,
          scopes,
          // The whole point: Cognito forwards straight to the provider instead
          // of rendering its own hosted sign-in page.
          identityProvider: IDP[provider],
        });
        // null = the user backed out (cancel / dismiss); say nothing
        if (!result) return;
        const fresh = await exchangeCode(discovery.tokenEndpoint, {
          clientId,
          code: result.code,
          redirectUri: result.redirectUri,
          codeVerifier: result.codeVerifier,
        });
        await persistOAuthTokens(fresh);
        // a social sign-in can't tell a new account from a returning one
        analytics?.track('login', { method: provider });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Sign-in failed');
      }
    },
    [oauth, discovery, clientId, scopes, persistOAuthTokens, analytics],
  );

  /* ---------- email + password ----------
     These rethrow so the calling screen can branch (e.g. route an unverified
     account to the verification step) while the banner copy is set here. */
  const signInWithPassword = useCallback(
    async (email: string, password: string) => {
      setError(null);
      try {
        const t = await cognitoApi.signInWithPassword(email.trim(), password);
        await applyTokens({ ...t, origin: 'password' });
        analytics?.track('login', { method: 'password' });
      } catch (e) {
        // an unverified account isn't a failure — the screen sends them to
        // verification, so don't flash an error banner on the way
        if (!(e instanceof CognitoError && e.code === 'UserNotConfirmedException')) {
          setError(e instanceof Error ? e.message : 'Sign-in failed');
        }
        throw e;
      }
    },
    [applyTokens, cognitoApi, analytics],
  );

  const wrap = useCallback(<A extends unknown[], R>(fn: (...args: A) => Promise<R>) => {
    return async (...args: A): Promise<R> => {
      setError(null);
      try {
        return await fn(...args);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Something went wrong.');
        throw e;
      }
    };
  }, []);

  const signUp = useMemo(
    () => wrap((email: string, password: string) => cognitoApi.signUp(email.trim(), password)),
    [wrap, cognitoApi],
  );

  const confirmSignUp = useMemo(
    () => wrap((email: string, code: string) => cognitoApi.confirmSignUp(email.trim(), code)),
    [wrap, cognitoApi],
  );

  const resendConfirmationCode = useMemo(
    () => wrap((email: string) => cognitoApi.resendConfirmationCode(email.trim())),
    [wrap, cognitoApi],
  );

  const forgotPassword = useMemo(
    () => wrap((email: string) => cognitoApi.forgotPassword(email.trim())),
    [wrap, cognitoApi],
  );

  const confirmForgotPassword = useMemo(
    () =>
      wrap((email: string, code: string, newPassword: string) =>
        cognitoApi.confirmForgotPassword(email.trim(), code, newPassword),
      ),
    [wrap, cognitoApi],
  );

  const signOut = useCallback(async () => {
    const t = tokensRef.current;
    const federated = t?.idToken ? isFederatedSession(t.idToken) : false;
    // First, while the session can still authenticate it: the front end's
    // chance to detach this device from the account — on mobile, removing its
    // push token, so the phone doesn't keep receiving this account's digests
    // after sign-out. Best-effort, and the token getter it is handed stores
    // nothing (idTokenWithoutStoring), so a refresh that lands late can't
    // re-save the session we are about to clear.
    if (t && adapters.auth?.beforeSignOut) {
      try {
        await adapters.auth.beforeSignOut(() =>
          idTokenWithoutStoring(t, { cognito: cognitoApi, clientId, discovery }),
        );
      } catch {
        // best-effort, as above
      }
    }
    tokensRef.current = null;
    await tokenStore.clear();
    setUser(null);
    setError(null);
    setStatus('signedOut');
    // Detach the billing SDK too, so the next account signed in on this device
    // does not inherit the previous one's purchases view. Best-effort, like
    // the revoke below.
    void billing?.forget();
    // Best-effort server-side revoke of the refresh token; local tokens are
    // already cleared, so failure here is harmless. The revoke endpoint is the
    // same for both origins — it's the pool's token, not the front door's.
    if (t?.refreshToken) {
      try {
        await revokeToken(discovery.revocationEndpoint, { clientId, token: t.refreshToken });
      } catch {
        // offline or unsupported — ignore
      }
    }

    /* Revoking kills the TOKENS. It does not touch the hosted UI's session
       COOKIE, which lives in the shared Safari/Chrome jar under the auth domain
       — so without this, "sign out" followed by "Continue with Google" silently
       signs the same person back in, with no account chooser. To the user, sign
       out looks broken.

       It has to be a browser round trip. There is no API: AWS says of
       AdminUserGlobalSignOut — which invalidates every id, access AND refresh
       token — "This operation doesn't clear the managed login session cookie.
       To clear the session ... direct their browser session to the logout
       endpoint." A fetch() from here carries none of those cookies and would be
       a no-op, and a server-side call from our Lambda even more so.

       ONLY for federated sessions: the password screens call InitiateAuth over
       plain HTTPS and never open a browser, so there is no cookie to clear and
       this would just put an iOS consent sheet ("… Wants to Use … to Sign In")
       in front of a sign-out that needs nothing.

       Best-effort like the revoke above: the user is already signed out
       locally, and a cancelled sheet or a dead network must not strand them on
       a screen that says otherwise.

       ⚠️ Does NOT sign them out of Google/Apple themselves — AWS is explicit
       that /logout leaves the IdP session alone. Google will show its account
       chooser; Apple may still pass an already-authorized user straight
       through. Fixing that would mean sending people to Apple's sign-out page,
       which is not a thing to do on a sign-out tap. See docs/social-login.md. */
    if (federated) {
      try {
        await oauth.endSession({ endSessionEndpoint: discovery.endSessionEndpoint, clientId });
      } catch {
        // cancelled, offline, or no browser — local sign-out already happened
      }
    }
  }, [adapters.auth, billing, oauth, tokenStore, cognitoApi, clientId, discovery]);

  /** Delete the Cognito user, then tear the session down locally.

     Runs LAST in the deletion sequence (the screen erases the server's rows
     first) because it is the step that destroys the credential: after it there
     is no session left to retry anything with. So a stale access token is
     refreshed rather than gambled on — Cognito rejects an expired one, and the
     failure would land at the worst possible moment.

     No revoke call, unlike signOut: revoking a token that belongs to a user who
     no longer exists has nothing to revoke. */
  const deleteAccount = useCallback(async () => {
    const t = tokensRef.current;
    if (!t) throw new CognitoError('NotAuthorizedException', 'You’re not signed in.');
    if (t.expiresAt - Date.now() <= REFRESH_SKEW_MS) await refreshSession(t);
    const accessToken = tokensRef.current?.accessToken;
    if (!accessToken) {
      throw new CognitoError('NotAuthorizedException', 'Your session expired — sign in and try again.');
    }
    await cognitoApi.deleteUser(accessToken);
    tokensRef.current = null;
    await tokenStore.clear();
    setUser(null);
    setError(null);
    setStatus('signedOut');
    // Same detach as signOut. The account is gone, so the billing alias for
    // this sub must not linger on the device — a later sign-up on the same
    // device would otherwise start against a stale identity.
    // ⚠️ This does NOT cancel the App Store subscription: Apple owns that
    // billing relationship and neither DELETE /v1/me nor RevenueCat can touch
    // it. The delete sheet's copy owes the user that sentence
    // (docs/subscriptions.md §10).
    void billing?.forget();
  }, [refreshSession, cognitoApi, tokenStore, billing]);

  const getIdToken = useCallback(async (): Promise<string | null> => {
    const t = tokensRef.current;
    if (!t) return null;
    if (t.expiresAt - Date.now() > REFRESH_SKEW_MS) return t.idToken;
    return refreshSession(t);
  }, [refreshSession]);

  const value = useMemo<AuthValue>(
    () => ({
      status,
      user,
      error,
      clearError,
      signInWithProvider,
      signInWithPassword,
      signUp,
      confirmSignUp,
      resendConfirmationCode,
      forgotPassword,
      confirmForgotPassword,
      signOut,
      deleteAccount,
      getIdToken,
    }),
    [
      status,
      user,
      error,
      clearError,
      signInWithProvider,
      signInWithPassword,
      signUp,
      confirmSignUp,
      resendConfirmationCode,
      forgotPassword,
      confirmForgotPassword,
      signOut,
      deleteAccount,
      getIdToken,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
